import { useCallback } from "react";
import { useGenerateNewsReport } from "../services/reportService";
import { useGetNewsReportData } from "../../news/services/newService";
import { exportTableToPdf } from "../../../shared/services/export/pdf/pdfExportService";
import { buildReportFileName } from "../utils/reportFilenames";
import { NEWS_PDF_COLUMNS, NEWS_PDF_OPTIONS } from "../config/newsPdfReport";
import type { NewReportDataItem } from "../../../api/client/types/new.types";

/**
 * Encapsulates the full generation logic for the news report.
 *
 * - `format === "pdf"`:  fetches all rows from the JSON endpoint, generates
 *   the PDF client-side via jsPDF, and returns `null` (no blob to download).
 * - `format === "excel"`: delegates to the backend report endpoint and
 *   returns the `Blob` for the caller to trigger a file download.
 *
 * @returns A stable async callback matching the `onGenerate` signature
 *          expected by `ReportGenerator`.
 */
export function useNewsReportHandler() {
  const generateExcel = useGenerateNewsReport();
  const fetchReportData = useGetNewsReportData();

  return useCallback(
    async (
      start_date: string,
      end_date: string,
      building_id?: string,
      format?: "excel" | "pdf",
      building_name?: string,
      report_type_id?: number,
    ): Promise<Blob | null> => {
      if (format === "pdf") {
        const { items } = await fetchReportData.mutateAsync({
          start_date,
          end_date,
          building_id,
        });

        const rowsForPdf = items.filter(
          (row) => row.report_type_name !== "Puesto de P.A / Botiquín",
        );
    
        await exportTableToPdf<NewReportDataItem>({
          ...NEWS_PDF_OPTIONS,
          columns: NEWS_PDF_COLUMNS,
          rows: rowsForPdf,
          fileName: buildReportFileName({
            reportType: "news",
            buildingName: building_name ?? "Todas las unidades residenciales",
            startDate: start_date,
            endDate: end_date,
            extension: "pdf",
          }),
          onError: (err) => console.error("Error generating news PDF:", err),
        });

        return null;
      }

      return generateExcel.mutateAsync({ start_date, end_date, building_id, format, report_type_id });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
